import { ColDef, ICellRendererParams } from 'ag-grid-community';
import styles from '../../AllAgents/UploadHistory/UploadHistoryDrawer.module.scss';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import Download from '@cp/src/assets/images/icons/Download';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import { useMemo } from 'react';
import { FILE_STATUS } from '../../AmeyoUtility/types';
import { fetchFailureReport, fetchOriginalFile } from './uploadHistoryDrawerActions';
import dayjs from 'dayjs';
import { DateTimeFormat } from '@cp/src/components/DateTimePicker/constants';

export const useColumnDefs = (): {
  columnDefs: ColDef[];
  handleFileButtonClick: (fileReferenceId: string, uploadStatus: string) => void;
} => {
  const handleFileButtonClick = (fileReferenceId: string, uploadStatus: string) => {
    if (uploadStatus === FILE_STATUS.COMPLETED || uploadStatus === FILE_STATUS.FAILED) {
      fetchOriginalFile(fileReferenceId);
    }
  };
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'totalLanCount',
        headerName: 'Total LANs',
        wrapHeaderText: true,
        minWidth: 80,
        maxWidth: 90,
        wrapText: true,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return data?.uploadStatus === FILE_STATUS.COMPLETED ||
            data?.uploadStatus === FILE_STATUS.INPROGRESS ||
            data?.uploadStatus === FILE_STATUS.INITIATED ? (
            <>
              <div>
                <Typography variant="p4" color="var(--grayscale-2)">
                  {data?.successCount + data?.failureCount}
                </Typography>
              </div>
            </>
          ) : (
            <Typography variant="p4" color="var(--grayscale-2)">
              -
            </Typography>
          );
        }
      },
      {
        field: 'successCount',
        headerName: 'Success',
        minWidth: 80,
        maxWidth: 90,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (
            data?.uploadStatus === FILE_STATUS.FAILED ||
            data?.uploadStatus === FILE_STATUS.INPROGRESS ||
            data?.uploadStatus === FILE_STATUS.INITIATED
          ) {
            return '-';
          }
          return <span>{data?.successCount}</span>;
        }
      },
      {
        field: 'failureCount',
        headerName: 'Failed',
        minWidth: 50,
        maxWidth: 70,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (
            data?.uploadStatus === FILE_STATUS.FAILED ||
            data?.uploadStatus === FILE_STATUS.INPROGRESS ||
            data?.uploadStatus === FILE_STATUS.INITIATED
          ) {
            return '-';
          }
          const textColor = data?.failureCount ? 'var(--red-base)' : 'var(--grayscale-2)';
          return <span style={{ color: textColor }}>{data?.failureCount}</span>;
        }
      },
      {
        field: 'uploadDateTime',
        headerName: 'Upload Date & Time',
        minWidth: 150,
        maxWidth: 200,
        wrapHeaderText: false,
        headerComponentParams: { width: 150 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor = 'var(--grayscale-3';
          return (
            <span style={{ color: textColor }}>
              {' '}
              {dayjs(data?.startTime).utc(data?.startTime).local().format(DateTimeFormat.DATE_TIME)}
            </span>
          );
        }
      },
      {
        field: 'originalFile',
        headerName: 'Original File',
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 100 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor =
            data?.uploadStatus ===
            (FILE_STATUS.INPROGRESS || data?.uploadStatus === FILE_STATUS.INITIATED)
              ? 'var(--green-base)'
              : 'var(--blue-base)';
          const icon =
            data?.uploadStatus === FILE_STATUS.COMPLETED ||
            data?.uploadStatus === FILE_STATUS.FAILED ? (
              <Download />
            ) : (
              <LoaderIcon
                size={20}
                borderWidth={3}
                borderColor="var(--greyscale-light)"
                fillColor="var(--green-base)"
              />
            );
          const cursorStyle =
            data?.uploadStatus === FILE_STATUS.INPROGRESS ||
            data?.uploadStatus === FILE_STATUS.INITIATED
              ? 'auto'
              : 'pointer';

          return (
            <Button
              variant="text"
              className={styles.buttonWrapper}
              style={{ color: textColor, cursor: cursorStyle }}
              onClick={() => handleFileButtonClick(data?.referenceId, data?.uploadStatus)}
              startAdornment={data?.uploadStatus && icon}
            >
              <span className={styles.buttonText}>
                {data?.uploadStatus &&
                (data?.uploadStatus === 'COMPLETED' || data?.uploadStatus === 'FAILED')
                  ? 'Download'
                  : 'In Progress'}
              </span>
            </Button>
          );
        }
      },
      {
        field: 'failureReport',
        headerName: 'Failure Report',
        wrapHeaderText: false,
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 80 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return data?.failureCount > 0 && data?.uploadStatus === FILE_STATUS.COMPLETED ? (
            <Button
              variant="text"
              className={styles.buttonWrapper}
              onClick={() => fetchFailureReport(data?.referenceId)}
              startAdornment={<Download />}
            >
              <span className={styles.buttonText}>Download</span>
            </Button>
          ) : data?.uploadStatus === FILE_STATUS.FAILED ? (
            <div>
              <div className={'flex'}>
                <Typography variant="p4" color="var(--navi-color-red-base)">
                  {'Failed'}
                </Typography>
                <div className="pl-[2px] pt-[2px]">
                  <ErrorIconOutline />
                </div>
              </div>
            </div>
          ) : (
            <Typography variant="p4" color="var(--grayscale-2)">
              -
            </Typography>
          );
        }
      }
    ],
    []
  );
  return { columnDefs, handleFileButtonClick };
};

export default useColumnDefs;
